/**
 * @fileoverview Trace ID generators using Web Crypto API
 * @module generators
 */

import { generateId } from '@puffin-core/id-generator';

/**
 * Generate a random hex string of specified length
 * @param {number} length - Length in characters (each byte = 2 chars)
 * @returns {string} Random hex string
 */
export function generateHexId(length) {
  const bytes = new Uint8Array(length / 2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Generate a W3C trace ID (32 hex characters)
 * @returns {string} Trace ID
 */
export function generateTraceId() {
  return generateHexId(32);
}

/**
 * Generate a W3C span ID (16 hex characters)
 * @returns {string} Span ID
 */
export function generateSpanId() {
  return generateHexId(16);
}

/**
 * Generate the browser's request-correlation id — a canonical `correlation` entity id,
 * `cor_<uuid>`.
 *
 * EPI-TYPE-RESTORATION-001 Q-17, owner ruling: *"Unify on `generateId('correlation')`
 * everywhere."* This used to mint `req_<16 hex>`, one of FOUR unrelated `req_` generators in the
 * repo, only one of which (`generateId('request')`, the access-request entity) was an entity id at
 * all. `req_` now means that one thing. The `X-Request-Id` header grows from 20 to 40 characters,
 * accepted in the ruling; the server side (`@puffin-core/logging`) mints the same type.
 *
 * @returns {string} A correlation id, e.g. `cor_0d15fa36-7b2e-4575-a960-b81f2b4c69a4`.
 */
export function generateRequestId() {
  return generateId('correlation');
}
